import { each } from 'lodash';

import { Logger } from '../logger/Logger';
import { Action } from './action';
import { RouteMissing } from './errors';
import { toUrlParams } from './helpers';
import { Scope, ScopeAttr } from './scope';
import { AttributeValues, UrlAttributes } from './types';

type Hook = {
  name: 'on:url-build';
  callback: (attributes: UrlAttributes) => UrlAttributes;
};

export class Routes {
  public actions: Action[];
  public scopes: Scope[];
  private logger: Logger;
  private hooks: Hook[];

  constructor(logger: Logger) {
    this.actions = [];
    this.scopes = [];
    this.logger = logger;
    this.hooks = [];
  }

  public get(routeName: string, type: string, attributes?: UrlAttributes): string {
    const action = this.actions.find((a: Action) => a.computedName === routeName && a.type === type);

    if (!action) {
      throw new RouteMissing(`Cannot find ${routeName}#${type}`);
    }

    let path = action.fullPath;
    const leftAttributes: UrlAttributes = {};
    const computedAttributes: UrlAttributes = this.hooks.reduce((acc, hook) => {
      if (hook.name === 'on:url-build') {
        return hook.callback(acc);
      }

      return acc;
    }, attributes ?? {});

    each(computedAttributes, (value: AttributeValues, key: string) => {
      const urlKey = `:${key}`;

      if (path.includes(urlKey)) {
        path = path.replace(urlKey, value.toString());
      } else {
        leftAttributes[key] = value;
      }
    });

    // Clean up:
    path = path
      // Remove any remaining :param? patterns
      .replace(/:[^/?]+\?/g, '')
      // Remove any double slashes
      .replace(/\/+/g, '/')
      // Remove trailing slash (unless it's the root path)
      .replace(/(.+)\/$/, '$1')
      // Remove the trailing ?
      .replace(/\?$/, '');

    if (Object.keys(leftAttributes).length === 0) {
      return path;
    }

    return `${path}?${toUrlParams(leftAttributes)}`;
  }

  public getRaw(routeName: string, type: string): string {
    const action = this.actions.find((a: Action) => a.computedName === routeName && a.type === type);

    if (!action) {
      throw new RouteMissing(`Cannot find ${routeName}#${type}`);
    }

    return action.fullPath;
  }

  public getUrl(routeName: string, type: string, host: string, attributes?: UrlAttributes): string {
    const path = this.get(routeName, type, attributes);

    return `${host}/${path}`;
  }

  /**
   * Build a scope
   *
   * @param attributes `ScopeAttr`
   */
  public scope(attributes: ScopeAttr, nestedFunction: (scope: Scope) => void): void {
    const scope = new Scope(attributes);
    nestedFunction(scope);

    this.scopes.push(scope);
    this.actions = this.actions.concat(scope.fetchActions());
  }

  /**
   * Add a hook to the routes
   */
  public addHook(hook: Hook): void {
    this.hooks.push(hook);
  }

  /**
   * Print all the routes
   *
   * Debugger method usually attached to the window object in development
   */
  public print(): void {
    this.actions.forEach((action: Action) => {
      this.logger.debug(action.details());
    });
  }
}
